import {
  AuthenticateWithRedirectCallback,
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  SignedIn,
  SignedOut,
  useUser,
} from '@clerk/clerk-react';
import { Button } from '@vrplatform/components';
import {
  TrpcProvider,
  useTrackingPage,
  useTrackingReset,
  TanstackQueryClient,
  TanstackQueryClientProvider,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Box } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { useState } from 'react';
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { EmptyViewWrapper, FullPageLoadingIndicator } from '../components';
import {
  SpotlightComponentProvider,
  SpotlightConfigProvider,
} from '../components/spotlight';
import { CLERK_PUBLISHABLE_KEY } from '../consts';
import { AppRoutes } from '../routes';
import { lazyWithRetry } from '../routes/dashboard/_utils';
import { VerifyTokenView } from '../views/invite-redirect/VerifyTokenView';
import { DBProvider } from './DBProvider';
import { LiveBlocksProvider } from './LiveBlocksProvider';
import { QueryDevTools } from './QueryDevTools';

const FailedInviteView = lazyWithRetry(
  () => import('../views/invite-redirect/FailedInvite')
);
const InvitationRedirect = lazyWithRetry(
  () => import('../views/invite-redirect/InvitationRedirect')
);
const SignInRoutes = lazyWithRetry(
  () => import('../views/onboarding/views/sign-in-up/SignInRoutes')
);

const queryClient = new TanstackQueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const page = useTrackingPage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: location pathname is not a dependency, but a trigger for tracking page
  useEffect(() => {
    page();
  }, [page, location.pathname]);

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to: string) => navigate(to)}
      routerReplace={(to: string) => navigate(to, { replace: true })}
    >
      <ClerkLoading>
        <AuthLoading />
      </ClerkLoading>

      <ClerkLoaded>
        <SignedIn>
          <AuthNewUserDelay>
            <SignedInRoutes />
          </AuthNewUserDelay>
        </SignedIn>
        <SignedOut>
          <SignOutRoutes />
        </SignedOut>
      </ClerkLoaded>
    </ClerkProvider>
  );
};

const SignedInRoutes = () => {
  return (
    <SentryRoutes>
      <Route path="hello" element={<Hello />} />

      <Route path="/invite-failed" element={<FailedInviteView />} />

      <Route
        path="/*"
        element={
          <SpotlightConfigProvider>
            <TrpcProvider queryClient={queryClient}>
              <TanstackQueryClientProvider client={queryClient}>
                <DBProvider>
                  <SpotlightComponentProvider>
                    <LiveBlocksProvider>
                      <Outlet />
                    </LiveBlocksProvider>
                    <QueryDevTools />
                  </SpotlightComponentProvider>
                </DBProvider>
              </TanstackQueryClientProvider>
            </TrpcProvider>
          </SpotlightConfigProvider>
        }
      >
        <Route path="invitation" element={<InvitationRedirect />} />
        <Route path="*" element={<AppRoutes />} />
      </Route>
    </SentryRoutes>
  );
};

const SignOutRoutes = () => {
  const reset = useTrackingReset();

  useEffect(() => {
    reset(undefined);
  }, [reset]);

  return (
    <SentryRoutes>
      <Route path="token-verify" element={<VerifyTokenView />} />
      <Route path="/sso-callback" element={<SSOCallback />} />
      <Route path="/invite-failed" element={<FailedInviteView />} />
      <Route path="/invitation" element={<ValidateInvitation />} />
      <Route path="/*" element={<SignIn />} />
    </SentryRoutes>
  );
};

const SignIn = () => (
  <SpotlightConfigProvider>
    <TrpcProvider queryClient={queryClient}>
      <TanstackQueryClientProvider client={queryClient}>
        <SignInRoutes />
      </TanstackQueryClientProvider>
    </TrpcProvider>
  </SpotlightConfigProvider>
);

const ValidateInvitation = () => (
  <SpotlightConfigProvider>
    <TrpcProvider queryClient={queryClient}>
      <TanstackQueryClientProvider client={queryClient}>
        <InvitationRedirect />
      </TanstackQueryClientProvider>
    </TrpcProvider>
  </SpotlightConfigProvider>
);

function Hello() {
  const navigate = useNavigate();
  React.useEffect(() => {
    setTimeout(() => {
      navigate('/');
    }, 3000);
  });
  return (
    <Box mt={'30vh'}>
      <LoadingIndicator isFullPageLoading message="Redirecting" />
    </Box>
  );
}

// Callback for Social logins like Google etc.
function SSOCallback() {
  // Handle the redirect flow by rendering the
  // prebuilt AuthenticateWithRedirectCallback component.
  // This is the final step in the custom OAuth flow
  return <AuthenticateWithRedirectCallback />;
}

const AuthNewUserDelay = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const isReady = !!user?.publicMetadata?.allowed_ids;
  const [timer, setTimer] = useState<number>(0);

  const isError = timer > 10;

  useEffect(() => {
    if (!isReady && !isError) {
      const timeout = setInterval(() => {
        setTimer((o) => o++);
        user?.reload();
      }, 1000);
      return () => clearInterval(timeout);
    }
  }, [isReady, isError, user?.reload]);

  if (isError) return <div>Something went wrong, please try again ...</div>;
  else if (!isReady) {
    return (
      <Box mt={'30vh'}>
        <LoadingIndicator isFullPageLoading message="Redirecting ..." />
      </Box>
    );
  }

  return <>{children}</>;
};

const AuthLoading = () => {
  const [timer, setTimer] = useState(0);
  const interval = useInterval(() => setTimer((s) => s + 1), 1000);

  const isError = timer > 20;

  useEffect(() => {
    interval.start();
    return interval.stop;
  }, []);

  if (isError)
    return (
      <EmptyViewWrapper
        title={"We'll be right back"}
        description={
          <>
            It looks like we're having some technical issues.
            <br />
            We are working on it and will be back soon.
          </>
        }
        actions={
          <Button
            variant="primary"
            leftIcon={'RefreshCwIcon'}
            sx={{ maxWidth: 200, width: '100%', marginInline: 'auto' }}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        }
      />
    );

  return (
    <FullPageLoadingIndicator
      message="Loading Dashboard..."
      showSignOut={false}
    />
  );
};
